<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chatbot: { type: Object, required: true },
});

const emit = defineEmits(['toggle', 'remove', 'edit']);

const { t } = useI18n();
// Only an administrator may flip the account-wide kill switch (see
// `chatbot-kill-switch`); the backend silently drops the field for anyone
// else, so this only needs to look and behave disabled, not hide the state.
const { isAdmin } = useAdmin();

const initials = computed(() => {
  const name = props.chatbot.botName || '?';
  return name
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
});
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-2"
  >
    <div class="flex items-start gap-3">
      <div
        class="flex items-center justify-center size-10 rounded-lg bg-n-brand/10 text-n-brand text-sm font-semibold flex-shrink-0"
      >
        {{ initials }}
      </div>
      <div class="flex flex-col min-w-0 flex-1">
        <span class="text-base font-medium text-n-slate-12 truncate">
          {{ chatbot.botName }}
        </span>
        <span v-if="chatbot.botFunction" class="text-sm text-n-slate-11 truncate">
          {{ chatbot.botFunction }}
        </span>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <span
          class="text-xs font-medium"
          :class="chatbot.active ? 'text-n-teal-11' : 'text-n-slate-10'"
        >
          {{ chatbot.active ? t('CHATBOTS.CARD.ON') : t('CHATBOTS.CARD.OFF') }}
        </span>
        <Switch
          :model-value="chatbot.active"
          :disabled="!isAdmin"
          @change="emit('toggle', chatbot.id)"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <span
        class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs"
        :class="
          chatbot.botUser
            ? 'bg-n-amber-3 text-n-amber-11'
            : 'bg-n-alpha-black2 text-n-slate-11'
        "
      >
        <span class="i-lucide-user-round size-3" />
        {{
          chatbot.botUser
            ? `${t('CHATBOTS.CARD.BOT_USER')}: ${chatbot.botUser.name}`
            : t('CHATBOTS.CARD.BOT_USER_UNASSIGNED')
        }}
      </span>
      <span
        v-if="chatbot.personality"
        class="px-2 py-1 rounded-md text-xs bg-n-alpha-black2 text-n-slate-11"
      >
        {{ chatbot.personality }}
      </span>
      <span
        v-if="chatbot.customerTreatment"
        class="px-2 py-1 rounded-md text-xs bg-n-alpha-black2 text-n-slate-11"
      >
        {{ chatbot.customerTreatment }}
      </span>
      <span
        v-if="chatbot.emojiUsage"
        class="px-2 py-1 rounded-md text-xs bg-n-alpha-black2 text-n-slate-11"
      >
        {{ t('CHATBOTS.CARD.EMOJI_USAGE', { value: chatbot.emojiUsage }) }}
      </span>
    </div>

    <p
      v-if="chatbot.mainObjective"
      class="text-sm text-n-slate-11 line-clamp-2 m-0"
    >
      {{ chatbot.mainObjective }}
    </p>

    <div class="flex items-center justify-end gap-2">
      <Button
        :label="t('CHATBOTS.CARD.EDIT')"
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-pencil"
        @click="emit('edit', chatbot)"
      />
      <Button
        :label="t('CHATBOTS.CARD.DELETE')"
        variant="ghost"
        color="ruby"
        size="sm"
        icon="i-lucide-trash-2"
        @click="emit('remove', chatbot.id)"
      />
    </div>
  </div>
</template>
